import { Command } from "cmdk";
import {
  Box,
  Dialog,
  DialogContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useHierarchyContext } from "../../contexts/HierarchyContext";

const SERVICE_SECTIONS = [
  { key: "health", label: "Health", path: "health" },
  { key: "health-checks", label: "Health Checks", path: "health-checks" },
  { key: "instances", label: "Instances", path: "instances" },
  { key: "alerts", label: "Alerts", path: "alerts" },
  { key: "metrics", label: "Metrics", path: "metrics" },
  { key: "traces", label: "Traces", path: "traces" },
  { key: "spans", label: "Spans", path: "spans" },
  { key: "logs", label: "Logs", path: "logs" },
  { key: "ingress-tokens", label: "Ingress Tokens", path: "ingress-tokens" },
] as const;

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { tenants, products, services, hierarchy } = useHierarchyContext();

  const items = useMemo(() => {
    const result: Array<{
      value: string;
      label: string;
      path: string;
      keywords: string[];
      group: string;
    }> = [];

    result.push({
      value: "home",
      label: "Home",
      path: "/home",
      keywords: ["dashboard", "start"],
      group: "Global",
    });
    result.push({
      value: "settings",
      label: "Settings",
      path: "/settings",
      keywords: ["preferences", "account"],
      group: "Global",
    });
    result.push({
      value: "create-tenant",
      label: "Create Tenant",
      path: "/tenants/new",
      keywords: ["new", "add"],
      group: "Create",
    });

    if (hierarchy?.tenant?.slug) {
      result.push({
        value: "create-product",
        label: "Create Product",
        path: `/tenants/${encodeURIComponent(hierarchy.tenant.slug)}/products/new`,
        keywords: ["new", "add", "product"],
        group: "Create",
      });
    }
    if (hierarchy?.product?.slug) {
      result.push({
        value: "create-service",
        label: "Create Service",
        path: `/products/${encodeURIComponent(hierarchy.product.slug)}/services/new`,
        keywords: ["new", "add", "service"],
        group: "Create",
      });
    }

    for (const t of tenants) {
      result.push({
        value: `tenant-${t.slug}`,
        label: t.name || t.slug,
        path: `/tenants/${encodeURIComponent(t.slug)}/products`,
        keywords: ["tenant", t.slug, t.name ?? ""].filter(Boolean),
        group: "Tenants",
      });
    }

    for (const p of products) {
      result.push({
        value: `product-${p.slug}`,
        label: p.name || p.slug,
        path: `/products/${encodeURIComponent(p.slug)}/services`,
        keywords: ["product", p.slug, p.name ?? "", p.tenantSlug].filter(Boolean),
        group: "Products",
      });
    }

    for (const s of services) {
      for (const section of SERVICE_SECTIONS) {
        const path = `/services/${encodeURIComponent(s.slug)}/${section.path}`;
        result.push({
          value: `service-${s.slug}-${section.key}`,
          label: `${s.name || s.slug} › ${section.label}`,
          path,
          keywords: [
            "service",
            s.slug,
            s.name ?? "",
            section.label.toLowerCase(),
            section.key,
          ].filter(Boolean),
          group: "Services",
        });
      }
    }

    return result;
  }, [tenants, products, services, hierarchy]);

  const groups = useMemo(() => {
    const map = new Map<string, typeof items>();
    for (const item of items) {
      const list = map.get(item.group) ?? [];
      list.push(item);
      map.set(item.group, list);
    }
    const order = ["Global", "Create", "Tenants", "Products", "Services"];
    return order.filter((g) => map.has(g)).map((g) => ({ name: g, items: map.get(g)! }));
  }, [items]);

  const valueToPath = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) {
      map.set(item.value, item.path);
    }
    return map;
  }, [items]);

  const handleSelect = useCallback(
    (value: string) => {
      const path = valueToPath.get(value);
      if (path) {
        navigate(path);
        onClose();
      }
    },
    [navigate, onClose, valueToPath],
  );

  const commandRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        const input = commandRef.current?.querySelector("input");
        if (input instanceof HTMLInputElement) input.focus();
      });
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          mt: fullScreen ? 0 : "10vh",
          maxHeight: fullScreen ? "100%" : "70vh",
          borderRadius: fullScreen ? 0 : 2,
          overflow: "hidden",
        },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: "rgba(0,0,0,0.7)" } },
        transition: {
          onEntered: () => {
            const input = commandRef.current?.querySelector("input");
            if (input instanceof HTMLInputElement) input.focus();
          },
        },
      }}
      aria-labelledby="command-palette-title"
      aria-describedby="command-palette-desc"
    >
      <DialogContent
        sx={{ p: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        <Box ref={commandRef} sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Box
            component="h2"
            id="command-palette-title"
            sx={{ ...visuallyHidden, m: 0, p: 0 }}
          >
            Search and navigate
          </Box>
          <Box
            id="command-palette-desc"
            sx={{ ...visuallyHidden, m: 0, p: 0 }}
          >
            Use arrow keys to navigate, Enter to select, Escape to close
          </Box>
        <Command
          label="Command palette"
          shouldFilter={true}
          className="om-command-palette"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              onClose();
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <SearchRounded sx={{ color: "text.secondary", fontSize: 20, mr: 1.5 }} aria-hidden />
            <Command.Input
              placeholder="Search routes, tenants, products, services…"
              aria-label="Search routes, tenants, products, and services"
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                fontSize: 16,
                color: "inherit",
              }}
            />
          </Box>
          <Command.List
            style={{
              maxHeight: 320,
              overflow: "auto",
              padding: "8px 0",
            }}
          >
            <Command.Empty>No results found.</Command.Empty>
            {groups.map(({ name, items: groupItems }) => (
              <Command.Group key={name} heading={name}>
                {groupItems.map((item) => (
                  <Command.Item
                    key={item.value}
                    value={item.value}
                    keywords={[item.label, ...item.keywords]}
                    onSelect={() => handleSelect(item.value)}
                  >
                    {item.label}
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
